// UI chrome translations: HU + EN, flat dot-keyed.
// Food names, food categories, food states, nutrient names, and nutrient
// categories live in foodTranslations.ts and nutrientTranslations.ts.

export type Lang = 'en' | 'hu'

export const translations: Record<Lang, Record<string, string>> = {
  en: {
    // App / header
    'app.tagline': 'When food clicks, Chi flows',
    'app.disclaimer': 'This system was not developed by a doctor — the data may be entirely incorrect. Consult your dietitian.',
    'app.mobileBanner': 'Due to data-dense tables, this app only works on desktop.',
    'app.title': 'KajChi - Your Food Planner',

    // Language toggle
    'toggle.switchToHu': 'Switch to Hungarian',
    'toggle.switchToEn': 'Switch to English',

    // Basket
    'basket.title': 'Basket',
    'basket.weight': 'Weight: {weight}g',
    'basket.total': 'Total: {price} Ft',
    'basket.per100g': 'Per 100g: {price} Ft',
    'basket.clearTooltip': 'Empty basket',
    'basket.clearLabel': 'Empty basket',
    'basket.empty': 'Basket is empty',
    'basket.emptyHint': 'Add foods from the food options',
    'basket.column.order': 'Order',
    'basket.column.food': 'Food',
    'basket.column.category': 'Category',
    'basket.column.state': 'State',
    'basket.column.totalWeight': 'Total',
    'basket.column.price': 'Price',
    'basket.column.portion': 'Portion',
    'basket.column.weight': 'Weight',
    'basket.column.delete': 'Delete',
    'basket.tooltip.up': 'Up',
    'basket.tooltip.down': 'Down',
    'basket.tooltip.removePortion': 'Remove 1 portion',
    'basket.tooltip.gramInput': 'Grams',
    'basket.tooltip.addPortion': 'Add 1 portion',
    'basket.prompt.addGrams': 'How many grams to add?',
    'basket.tooltip.addGramsBtn': 'Add specified grams',
    'basket.tooltip.removeFromBasket': 'Remove from basket',

    // Suggestions
    'suggestions.title': 'Food Options',
    'suggestions.available': 'Available: {count}',
    'suggestions.resetCustomTooltip': 'Reset portion sizes and prices',
    'suggestions.resetCustomLabel': 'Reset',
    'suggestions.searchPlaceholder': 'Search foods...',
    'suggestions.clearSearch': 'Clear search',
    'suggestions.sort.name': 'By name',
    'suggestions.sort.category': 'By category',
    'suggestions.sort.nutrientPer100g': 'By nutrient/100g',
    'suggestions.sort.nutrientPerPortion': 'By nutrient/portion',
    'suggestions.filter.all': 'All',
    'suggestions.hideFodmap': 'Hide high FODMAP',
    'suggestions.selectNutrient': 'Choose a nutrient...',
    'suggestions.noResults': 'No results',
    'suggestions.column.food': 'Food',
    'suggestions.column.category': 'Category',
    'suggestions.column.state': 'State',
    'suggestions.column.packageSize': 'Package',
    'suggestions.column.price': 'Price',
    'suggestions.column.portion': 'Portion',
    'suggestions.column.portionPrice': 'Per portion',
    'suggestions.column.content': 'Content',
    'suggestions.column.basket': 'Basket',
    'suggestions.tooltip.packageSize': 'Package size in grams',
    'suggestions.tooltip.priceForint': 'Price in forints',
    'suggestions.tooltip.portionSize': 'Portion size in grams',
    'suggestions.tooltip.resetDefault': 'Reset to default',
    'suggestions.tooltip.removeFood': 'Remove from basket: {name}',
    'suggestions.tooltip.addFood': 'Add 1 portion ({grams}g): {name}',
    'suggestions.unit.per100g': '{unit}/100g',
    'suggestions.unit.perPortion': '{unit}/portion',

    // Indicators
    'indicators.title': 'Nutrient Goals',
    'indicators.nutrientsLabel': 'Nutrients:',
    'indicators.okLabel': 'OK?:',
    'indicators.successLabel': 'Success:',
    'indicators.resetCustomTooltip': 'Reset custom nutrient settings',
    'indicators.resetCustomLabel': 'Reset',
    'indicators.toggleCategory.hide': 'Hide: {category} nutrients',
    'indicators.toggleCategory.show': 'Show: {category} nutrients',
    'indicators.hideIgnored': 'Hide ignored',
    'indicators.hideIgnoredTooltip': 'Hide ignored nutrients',
    'indicators.showAllNutrientsTooltip': 'Show all nutrients (including ignored)',
    'indicators.empty': 'No indicators',
    'indicators.column.nutrient': 'Nutrient',
    'indicators.column.category': 'Category',
    'indicators.column.progress': 'Progress',
    'indicators.column.value': 'Value',
    'indicators.column.algorithm': 'Algorithm',
    'indicators.column.min': 'Min',
    'indicators.column.target': 'Target',
    'indicators.column.max': 'Max',
    'indicators.column.ok': 'OK?',
    'indicators.tooltip.resetDefault': 'Reset to default',

    // Algorithms
    'algorithm.reachMinDontExceedMax': 'Min ↔ Max',
    'algorithm.reachMin': 'Reach Min',
    'algorithm.dontExceedMax': 'Max Limit',
    'algorithm.ignore': 'Ignore',

    // Nutrient progress bar tooltips
    'progress.belowMin': 'Below minimum (< {value})',
    'progress.optimalRange': 'Optimal range ({min} - {max})',
    'progress.aboveMax': 'Above maximum (> {value})',
    'progress.aboveMin': 'Above minimum (≥ {value})',
    'progress.belowMax': 'Below maximum (< {value})',
    'progress.noConstraint': 'No constraint or ignored',

    // Profile editor
    'profile.age': 'Age',
    'profile.sex': 'Sex',
    'profile.sex.male': 'Male',
    'profile.sex.female': 'Female',
    'profile.weight': 'Weight',
    'profile.unitKg': 'kg',
    'profile.height': 'Height',
    'profile.unitCm': 'cm',
    'profile.activity': 'Activity',
    'profile.activity.sedentary': 'Sedentary (desk job)',
    'profile.activity.light': 'Light (1-3 sessions/week)',
    'profile.activity.moderate': 'Moderate (3-5 sessions/week)',
    'profile.activity.active': 'Active (6-7 sessions/week)',
    'profile.activity.veryActive': 'Very active (physical job + training)',

    // Global actions
    'globalActions.resetAllTooltip': 'Full reset: basket, profile, nutrients, portion sizes, prices',
    'globalActions.resetAllLabel': 'Reset',
    'globalActions.exportTooltip': 'Export preferences',
    'globalActions.exportLabel': 'Save',
    'globalActions.importTooltip': 'Import preferences',
    'globalActions.importLabel': 'Load',
    'globalActions.modal.exportTitle': 'Save preferences',
    'globalActions.modal.importTitle': 'Load preferences',
    'globalActions.modal.exportInstructions': 'Copy this string to save your current nutrient preferences and basket contents:',
    'globalActions.modal.copyToClipboard': 'Copy to clipboard',
    'globalActions.modal.importInstructions': 'Paste a saved preferences string below:',
    'globalActions.modal.importPlaceholder': 'Paste preferences here...',
    'globalActions.modal.invalidFormat': 'Invalid preferences format. Please check the data and try again.',
    'globalActions.modal.cancel': 'Cancel',
    'globalActions.modal.loadButton': 'Load preferences',
  },

  hu: {
    // App / header
    'app.tagline': 'Ha klappol a kaja, megjön a Chi',
    'app.disclaimer': 'A rendszert nem orvos fejlesztette, az adatok teljesen hibásak lehetnek. Konzultálj dietetikusoddal.',
    'app.mobileBanner': 'Adatsűrű táblázatok miatt ez az app csak asztali gépen működik.',
    'app.title': 'KajChi - A Kaja Terveződ',

    // Language toggle
    'toggle.switchToHu': 'Váltás magyarra',
    'toggle.switchToEn': 'Switch to English',

    // Basket
    'basket.title': 'Kosár',
    'basket.weight': 'Súly: {weight}g',
    'basket.total': 'Összesen: {price} Ft',
    'basket.per100g': '100g-ra: {price} Ft',
    'basket.clearTooltip': 'Kosár kiürítése',
    'basket.clearLabel': 'Kosár ürítése',
    'basket.empty': 'A kosár üres',
    'basket.emptyHint': 'Adj hozzá ételeket az étel opciókból',
    'basket.column.order': 'Sorrend',
    'basket.column.food': 'Étel',
    'basket.column.category': 'Kategória',
    'basket.column.state': 'Állapot',
    'basket.column.totalWeight': 'Összesen',
    'basket.column.price': 'Ár',
    'basket.column.portion': 'Adag',
    'basket.column.weight': 'Súly',
    'basket.column.delete': 'Törlés',
    'basket.tooltip.up': 'Fel',
    'basket.tooltip.down': 'Le',
    'basket.tooltip.removePortion': '1 adag eltávolítása',
    'basket.tooltip.gramInput': 'Gramm',
    'basket.tooltip.addPortion': '1 adag hozzáadása',
    'basket.prompt.addGrams': 'Hány grammot adj hozzá?',
    'basket.tooltip.addGramsBtn': 'Megadott gramm hozzáadása',
    'basket.tooltip.removeFromBasket': 'Eltávolítás a kosárból',

    // Suggestions
    'suggestions.title': 'Étel Opciók',
    'suggestions.available': 'Elérhető: {count}',
    'suggestions.resetCustomTooltip': 'Adagméretek és árak visszaállítása',
    'suggestions.resetCustomLabel': 'Visszaállítás',
    'suggestions.searchPlaceholder': 'Ételek keresése...',
    'suggestions.clearSearch': 'Keresés törlése',
    'suggestions.sort.name': 'Név szerint',
    'suggestions.sort.category': 'Kategória szerint',
    'suggestions.sort.nutrientPer100g': 'Tápanyag/100g szerint',
    'suggestions.sort.nutrientPerPortion': 'Tápanyag/adag szerint',
    'suggestions.filter.all': 'Összes',
    'suggestions.hideFodmap': 'Magas FODMAP elrejtése',
    'suggestions.selectNutrient': 'Válassz tápanyagot...',
    'suggestions.noResults': 'Nincs találat',
    'suggestions.column.food': 'Étel',
    'suggestions.column.category': 'Kategória',
    'suggestions.column.state': 'Állapot',
    'suggestions.column.packageSize': 'Kiszerelés',
    'suggestions.column.price': 'Ár',
    'suggestions.column.portion': 'Adag',
    'suggestions.column.portionPrice': 'Adagár',
    'suggestions.column.content': 'Tartalom',
    'suggestions.column.basket': 'Kosár',
    'suggestions.tooltip.packageSize': 'Kiszerelés grammban',
    'suggestions.tooltip.priceForint': 'Ár forintban',
    'suggestions.tooltip.portionSize': 'Adag mérete grammban',
    'suggestions.tooltip.resetDefault': 'Visszaállítás alapértelmezettre',
    'suggestions.tooltip.removeFood': 'Eltávolítás a kosárból: {name}',
    'suggestions.tooltip.addFood': '1 adag ({grams}g) hozzáadása: {name}',
    'suggestions.unit.per100g': '{unit}/100g',
    'suggestions.unit.perPortion': '{unit}/adag',

    // Indicators
    'indicators.title': 'Tápanyagcélok',
    'indicators.nutrientsLabel': 'Tápanyagok:',
    'indicators.okLabel': 'Ok?:',
    'indicators.successLabel': 'Siker:',
    'indicators.resetCustomTooltip': 'Egyéni tápanyag-beállítások visszaállítása',
    'indicators.resetCustomLabel': 'Visszaállítás',
    'indicators.toggleCategory.hide': 'Elrejtés: {category} tápanyagok',
    'indicators.toggleCategory.show': 'Megjelenítés: {category} tápanyagok',
    'indicators.hideIgnored': 'Mellőzöttek elrejtése',
    'indicators.hideIgnoredTooltip': 'Mellőzött tápanyagok elrejtése',
    'indicators.showAllNutrientsTooltip': 'Minden tápanyag megjelenítése (mellőzöttekkel együtt)',
    'indicators.empty': 'Nincs indikátor',
    'indicators.column.nutrient': 'Tápanyag',
    'indicators.column.category': 'Kategória',
    'indicators.column.progress': 'Haladás',
    'indicators.column.value': 'Érték',
    'indicators.column.algorithm': 'Algoritmus',
    'indicators.column.min': 'Min',
    'indicators.column.target': 'Cél',
    'indicators.column.max': 'Max',
    'indicators.column.ok': 'Ok?',
    'indicators.tooltip.resetDefault': 'Visszaállítás alapértelmezettre',

    // Algorithms
    'algorithm.reachMinDontExceedMax': 'Min ↔ Max',
    'algorithm.reachMin': 'Min elérése',
    'algorithm.dontExceedMax': 'Max korlát',
    'algorithm.ignore': 'Mellőz',

    // Nutrient progress bar tooltips
    'progress.belowMin': 'Minimum alatt (< {value})',
    'progress.optimalRange': 'Optimális tartomány ({min} - {max})',
    'progress.aboveMax': 'Maximum felett (> {value})',
    'progress.aboveMin': 'Minimum felett (≥ {value})',
    'progress.belowMax': 'Maximum alatt (< {value})',
    'progress.noConstraint': 'Nincs korlátozás vagy mellőzött',

    // Profile editor
    'profile.age': 'Kor',
    'profile.sex': 'Nem',
    'profile.sex.male': 'Férfi',
    'profile.sex.female': 'Nő',
    'profile.weight': 'Súly',
    'profile.unitKg': 'kg',
    'profile.height': 'Magasság',
    'profile.unitCm': 'cm',
    'profile.activity': 'Aktivitás',
    'profile.activity.sedentary': 'Alacsony (irodai munka)',
    'profile.activity.light': 'Enyhe (heti 1-3x edzés)',
    'profile.activity.moderate': 'Közepes (heti 3-5x edzés)',
    'profile.activity.active': 'Magas (heti 6-7x edzés)',
    'profile.activity.veryActive': 'Nagyon magas (fizikai munka + edzés)',

    // Global actions
    'globalActions.resetAllTooltip': 'Teljes visszaállítás: kosár, profil, tápanyagok, adagméretek, árak',
    'globalActions.resetAllLabel': 'Alaphelyzet',
    'globalActions.exportTooltip': 'Preferenciák exportálása',
    'globalActions.exportLabel': 'Mentés',
    'globalActions.importTooltip': 'Preferenciák importálása',
    'globalActions.importLabel': 'Betöltés',
    'globalActions.modal.exportTitle': 'Preferenciák mentése',
    'globalActions.modal.importTitle': 'Preferenciák betöltése',
    'globalActions.modal.exportInstructions': 'Másold ezt a szöveget a jelenlegi tápanyag-preferenciák és kosártartalom mentéséhez:',
    'globalActions.modal.copyToClipboard': 'Másolás vágólapra',
    'globalActions.modal.importInstructions': 'Illeszd be a mentett preferenciák szövegét alább:',
    'globalActions.modal.importPlaceholder': 'Preferenciák beillesztése ide...',
    'globalActions.modal.invalidFormat': 'Érvénytelen preferencia formátum. Kérlek ellenőrizd az adatokat és próbáld újra.',
    'globalActions.modal.cancel': 'Mégse',
    'globalActions.modal.loadButton': 'Preferenciák betöltése',
  },
}

// Build-time guarantee that both locales have identical key sets.
type EnKeys = keyof typeof translations.en
type HuKeys = keyof typeof translations.hu
const _enHasAllHu: Record<HuKeys, string> = translations.en
const _huHasAllEn: Record<EnKeys, string> = translations.hu
void _enHasAllHu
void _huHasAllEn

export function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`))
}
